import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';

import { tap, map, catchError } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { PatientFile, CrudListResponses } from '@app/shared/models';
import { NgxSpinnerService } from 'ngx-spinner';

import swal from 'sweetalert2';
import { AccountService } from '@app/core/account/services/account.service';
import { EXPIRATION_FOR_DIGITAL_OCEAN, EXPIRATION_FOR_CACHE } from '@app/shared/constants/Expiration.constants';
import { LocalStorageService } from 'angular-web-storage';
import { GetAPIResponse } from '@app/shared/models/crud-responses';
import { PAGINATED } from '@app/shared/constants/RecordTypeConstant';
import { AccountAccessGuardService } from '../helpers/account-access-guard.service';

@Injectable({
  providedIn: 'root'
})
export class FilesService {
  files: Observable<PatientFile[]>;
  private _files: PatientFile[] = [];
  file: PatientFile;
  public gettingRecords: boolean = false;
  params: any = {
		per_page: 100,
		limit: 100,
		page: 1,
		total: 0,
		sortField: 'added_dt',
		sortDirection: 'desc',
		recordType: PAGINATED
	};
  records: Observable<[]>;
	private readonly _record = new BehaviorSubject<GetAPIResponse>({});
	public record$ = this._record.asObservable();
	private _records;
  constructor(
    private http: HttpClient,
    private spinner: NgxSpinnerService,
    private accountSvc: AccountService,
    private accountAccessGuardService: AccountAccessGuardService,
    private storage: LocalStorageService ) {
    // this.getInitRecords().subscribe();
  }

  getPatientFiles(options?: any) {
    this.gettingRecords = true;
    let params = new HttpParams();
    Object.keys(options).forEach(function (key) {
      params = params.append(key.toString(), options[key]);
    });
    return this.http.get(`${environment.apiUrlV2}/file/patient-files`, { params }).pipe(
      tap((data: GetAPIResponse) => {
        this.gettingRecords = false;
        this.files = of(<PatientFile[]>data.data);
        this._files = <PatientFile[]>data.data;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getImgToDataUrl(url) {
    return fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  }


  private static readonly FAILED_FILE_TTL = 5 * 60 * 1000; // 5 minutes
  private failedFiles: Map<string, number> = new Map();

  private isFileFailed(filename: string): boolean {
    const failedAt = this.failedFiles.get(filename);
    if (!failedAt) return false;
    if (Date.now() - failedAt > FilesService.FAILED_FILE_TTL) {
      this.failedFiles.delete(filename);
      return false;
    }
    return true;
  }

  private markFileFailed(filename: string): void {
    if (filename) {
      this.failedFiles.set(filename, Date.now());
    }
  }

  // new endpoint for digital ocean
  /**
   * Fetches file as a data: URL via same-origin API (avoids CORS on presigned object storage URLs).
   * Used e.g. for PDF.js in consent print. Response shape matches /file/patient-files/base64.
   * The API must enforce the same access control as other patient-file paths for `filename` (storage key).
   */
  getPatientFileDataUrlForConsentPrint(filename: string): Observable<string | null> {
    return this.getPatientFileDataUrl(filename);
  }

  getPatientFileDataUrl(filename: string): Observable<string | null> {
    if (!filename || !String(filename).trim()) {
      return of(null);
    }
    return this.http
      .get(`${environment.apiUrlV2}/file/patient-files/base64`, { params: { filename: String(filename).trim() } })
      .pipe(
        map((res: any) => {
          const s = res?.filename ?? res?.data?.filename;
          return typeof s === 'string' && s.startsWith('data:') ? s : null;
        }),
        catchError(() => of(null))
      );
  }

  getFilenameDataUri(filename: string): Observable<any> {
    if (this.isFileFailed(filename)) {
      return of({ filename: null, uri: null });
    }
    const cachedUri: any = localStorage.getItem('uri_' + filename);
    if (cachedUri) {
      return of({ filename: filename, uri: cachedUri });
    }
    let params: any = {
      filename: filename,
      expiration_time: EXPIRATION_FOR_DIGITAL_OCEAN,
    };
    return this.http.get(`${environment.apiUrlV2}/file/retrieve-file`, { params: params }).pipe(
      tap((resp: any) => {
        if (resp.uri) {
          try {
            localStorage.setItem('uri_' + filename, resp.uri);
          } catch (e) {
            if (e instanceof DOMException && e.code === DOMException.QUOTA_EXCEEDED_ERR) {
              console.warn('LocalStorage quota exceeded. Skipping file caching.');
            }
          }
        }
      }),
      catchError(() => {
        this.markFileFailed(filename);
        return of({ filename: null, uri: null });
      })
    );
  }

  getPresignedFilenameDataUriV2(filename: string): Observable<any> {
    if (this.isFileFailed(filename)) {
      return of({ filename: null });
    }
    const cachedFile: any = localStorage.getItem(filename);
    if (cachedFile && cachedFile !== 'null' && cachedFile.startsWith('data:')) {
      return of({ filename: cachedFile });
    } else if (cachedFile && !cachedFile.startsWith('data:')) {
      // clear bad cached values (e.g. "null" string from previous failures)
      localStorage.removeItem(filename);
    }
    return this.getFileBase64(filename).pipe(
      catchError(() => {
        this.markFileFailed(filename);
        return of({ filename: null });
      })
    );
  }

  private getFileBase64(filename: string): Observable<any> {
    return this.http.get(`${environment.apiUrlV2}/file/patient-files/base64`, {
      params: { filename }
    }).pipe(
      tap((result: any) => {
        if (result?.filename && result.filename.startsWith('data:')) {
          try {
            localStorage.setItem(filename, result.filename);
          } catch (e) {
            if (e instanceof DOMException && e.code === DOMException.QUOTA_EXCEEDED_ERR) {
              console.warn('LocalStorage quota exceeded. Skipping file caching.');
            }
          }
        }
      }),
      catchError(() => {
        this.markFileFailed(filename);
        return of({ filename: null });
      })
    );
  }

  createRecord(data: any): Observable<PatientFile> {
 
    if (!this.accountSvc.canUploadFiles) {
      swal({
        title: 'Upload error!',
        text: 'You have already used up your free 10GB storage. Please subscribe for additional storage to continue uploading files.',
        type: 'warning',
        showCancelButton: false,
        confirmButtonColor: '#3085d6'
      });
      return of(null);
    }

    return this.accountAccessGuardService.accountGuardRestriction(() => {
      this.spinner.show();
  
      return this.http.post(`${environment.apiUrlV2}/file/patient-files`, data).pipe(
        tap((res: any) => {
          this.spinner.hide();
  
          data.id = res.id;
          if (res.account_id) {
            data.account_id = res.account_id;
          }
          if (res.status) {
            data.status = res.status;
          }
          if (res.filenames) {
            data.filenames = res.filenames;
          }
          data.remarks = data.get('remarks');
          data.added_dt = new Date();
          this._files.splice(0, 0, <PatientFile>data);
          this.file = <PatientFile>data;
        }),
        catchError((error) => {
          this.spinner.hide();
          
          swal({
            title: 'Save error!',
            text: error.error?.message || 'An error occurred.',
            type: 'warning',
            showCancelButton: false,
            confirmButtonColor: '#3085d6'
          });
          return of(null);
        })
      );
    });
  }
  deleteRecord(id: number): Observable<boolean> {
   
    const url = `${environment.apiUrlV2}/file/patient-files/${id}`;
    return this.accountAccessGuardService.accountGuardRestriction(() => this.http.delete(url).pipe(
      tap(() => {
        let file = this._files.find((row) => row.id === id);
        if (file) {
          this._files.splice(this._files.indexOf(file), 1);
        }
      }),
      map(() => true),
      catchError((error) => {
        swal({
          title: 'Delete error!',
          text: error.error.message,
          type: 'warning',
          showCancelButton: false,
          confirmButtonColor: '#3085d6'
        });
        return throwError(error);
      })
    ));
  }

  updateRecord(id: number, data: any): Observable<PatientFile> {
    
    this.spinner.show();
      return this.accountAccessGuardService.accountGuardRestriction(() => this.http.post(`${environment.apiUrlV2}/file/patient-files/${id}`, data).pipe(
      tap(
        (res: any) => {
          this.spinner.hide();
          let files = this._files.find((row) => row.id == id);
          if (files) {
            files = <PatientFile>Object.assign(files, data);
          }
          this.file = <PatientFile>data;
        },
        (error) => {
          swal({
            title: 'Update error!',
            text: error.error.message,
            type: 'warning',
            showCancelButton: false,
            confirmButtonColor: '#3085d6'
          });
          return of(null);
        }
      )
    ));
  }

  getBatchFileUrls(filenames: string[]): Observable<any[]> {
    if (!filenames || filenames.length === 0) {
      return of([]);
    }
    const validFilenames = filenames.filter(f => f && !this.isFileFailed(f));
    if (validFilenames.length === 0) {
      return of([]);
    }
    return this.http.post(`${environment.apiUrlV2}/file/batch-urls`, {
      filenames: validFilenames,
      expiration_time: EXPIRATION_FOR_DIGITAL_OCEAN
    }).pipe(
      tap((results: any[]) => {
        results.forEach((item: any) => {
          if (item.uri && item.filename) {
            try {
              localStorage.setItem('uri_' + item.filename, item.uri);
              this.storage.set(item.filename, item.uri, EXPIRATION_FOR_CACHE, 'd');
            } catch (e) {}
          } else if (item.filename) {
            this.markFileFailed(item.filename);
          }
        });
      }),
      catchError(() => of([]))
    );
  }

  setParamsForLaboratoryFiles(params: any) {
    this.params = params;
  }
  refreshListForLaboratoryFiles() {
      this._record.next(null);
      this.getInitialRecordsForLaboratoryFiles(this.params).subscribe();
  }
  getInitialRecordsForLaboratoryFiles(params?: any) {
      this.gettingRecords = true;
      let url = environment.apiUrlV2 + '/v2/patients/laboratory-files';

      return this.http.get(`${url}`, {params: params}).pipe(
          tap(
              (data: GetAPIResponse) => {
                  this.gettingRecords = false;
                  this._record.next(data);
                  this.files = of(<PatientFile[]>data.data);
                  this._files = <PatientFile[]>data.data;
              }
          ),
          catchError(error => {
              return throwError(error);
          })
      );
  }
}
